import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Products } from 'src/app/models/Product.model';
import { PagesService } from 'src/app/services/pages.service';
import { ProductsService } from 'src/app/services/products.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * GestioneProdottiDettagliComponent — products filtered by category.
 *
 * Reached by clicking a leaf in the Categorie view of
 * `gestione-prodotti-list`. The URL params (`family`, `subfamily`,
 * `group`, `subgroup`) define the filter. The page shows the matching
 * products in a Diakonix table; click a row → detail modal (B.3).
 *
 * Phase 1.5 / v2.7.0 — TS cleaned up:
 *   • dropped kendo `GridDataResult` + `PageChangeEvent` (table is
 *     now native Diakonix, no virtual scroll),
 *   • dropped FormBuilder + productsDetailForm (zero usages — the
 *     template never bound to a form),
 *   • dropped DropdownService + OrdersService (zero usages),
 *   • dropped ProductPosition placeholder (warehouse position lives
 *     in the detail modal, not on this page),
 *   • dropped ~10 console.log/debug,
 *   • dropped `edit()` / `editFields()` / `editForm()` / `newFields()`
 *     / `clearForm()` / `open()` / `close()` (zero call sites in
 *     either template or routing),
 *   • dropped duplicate `getProducts1()` / `getProducts()` (only
 *     `getProducts1` was used in ngOnInit; renamed to `loadAll`),
 *   • unified `showDetails` into `openDetail()`.
 */
@Component({
  selector: 'app-gestione-prodotti-dettagli',
  templateUrl: './gestione-prodotti-dettagli.component.html',
  styleUrls: ['./gestione-prodotti-dettagli.component.scss'],
  standalone: false,
})


export class GestioneProdottiDettagliComponent implements OnInit {

  private readonly productServ = inject(ProductsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly pagesServ = inject(PagesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  public readonly closeSrv = inject(OpenModalService);

  @Input() opened = false;
  @Input() isProdOpen = false;

  // ── Data ────────────────────────────────────────────────────────
  public products: Products.IProductDetail[] = [];
  public gridData: Products.IProductDetail[] = [];

  // ── URL params (the active category filter) ─────────────────────
  family: string | null = null;
  subFamily: string | null = null;
  group: string | null = null;
  subGroup: string | null = null;

  // ── UI state ────────────────────────────────────────────────────
  public loading = false;
  public filterTerm = '';
  public clickedId: string | null = null;

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loading = true;
    this.readParams();
    this.loadAll();
  }

  private readParams(): void {
    this.family = this.readParam('family');
    this.subFamily = this.readParam('subfamily');
    this.group = this.readParam('group');
    this.subGroup = this.readParam('subgroup');
  }

  /**
   * 🚨 FIX v2.11.2 (Q4): le route di categoria sono strutturate come
   * `path: ':family', children: [{ path: '', component: this }]` —
   * Angular non eredita i paramMap dai parent route per default su
   * un child con `path: ''`. `route.snapshot.paramMap.get('family')`
   * sul child ritornava sempre `null` → `family ?? 'A'` → l'endpoint
   * chiamato era `/get_products/A/A/A/A` (tutti i prodotti, payload
   * enorme, stallo). Cammino l'albero dei parent fino a trovare il
   * param. Soluzione locale, no impatto su altre route.
   */
  private readParam(name: string): string | null {
    let r: ActivatedRoute | null = this.route;
    while (r) {
      const v = r.snapshot.paramMap.get(name);
      if (v) return v;
      r = r.parent;
    }
    return null;
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loaders.
  // ──────────────────────────────────────────────────────────────────

  /**
   * Loads the inventory filtered by category params from the route.
   *
   * 🚨 FIX v2.11.2 (Q4): Chunk B.2 originale chiamava `getProducts()`
   * (senza params → endpoint `/get_products/A/A/A/A` → ritorna TUTTO
   * l'inventario). Il payload è enorme (decine di MB con centinaia di
   * prodotti) e su alcuni network/browser non si completa entro il
   * timeout → subscribe non emette → `loading` resta true → skeleton
   * eterno. Inoltre semanticamente sbagliato: l'utente è arrivato su
   * questa pagina cliccando una categoria (es. tradizionale → roof),
   * deve vedere SOLO quei prodotti, non tutti. Switch a
   * `getProductCategories(family, subFamily, group, subGroup)` che
   * usa i params reali della route. Aggiunto catchError per garantire
   * che il loading flag si resetti sempre.
   */
  private loadAll(): void {
    this.productServ
      .getProductCategories(
        this.family ?? 'A',
        this.subFamily ?? 'A',
        this.group ?? 'A',
        this.subGroup ?? 'A',
      )
      .pipe(
        catchError(() => of<Products.IProductDetail[]>([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          this.products = result ?? [];
          this.gridData = [...this.products];
          this.loading = false;
          // 🚨 FIX v2.11.2 (Q4 polish): detectChanges esplicito post-subscribe.
          // Stesso pattern del CD bug fixato ieri sul detail preparazione-ordini:
          // in alcuni casi edge HttpClient.subscribe non triggera CD automatica
          // → skeleton resta visibile finché un evento DOM forza un cycle.
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Search.
  // ──────────────────────────────────────────────────────────────────

  searchProducts(): void {
    if (!this.filterTerm?.trim()) {
      this.loadAll();
      return;
    }
    this.productServ
      .searchCategoryProducts(
        this.filterTerm,
        this.family ?? '',
        this.subFamily ?? '',
        this.group ?? '',
        this.subGroup ?? '',
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.products = res ?? [];
          this.gridData = res ?? [];
          this.cdr.detectChanges();
        },
      });
  }

  clearSearch(): void {
    this.filterTerm = '';
    this.loadAll();
  }

  // ──────────────────────────────────────────────────────────────────
  // Detail: click product → open Diakonix detail modal (Chunk B.3).
  // ──────────────────────────────────────────────────────────────────

  openDetail(productId: string, productDesc: string): void {
    const slug = this.pagesServ
      .replaceAll(productDesc?.trim() ?? '', ' ', '-');
    this.clickedId = productId;
    this.closeSrv.openProd();
    this.router.navigate([`${slug}-${productId.trim()}`], {
      relativeTo: this.route,
    });
  }
  
}
